import { CellEventFormatterProps } from '../../utils'
import { FbLink } from 'components/FbUI/FbLink'
import { useMemo } from 'react'
import { DefaultAddOrRemoveEvent } from './DefaultAddOrRemoveEvent'
import { renderTagPills } from './renderTagPills'
import useApiService from 'services/api'
import { Tag } from 'models/tags'
import { CompanyDistributor, CompanyGPO } from 'models/companies'

export const CompanyAddOrRemoveEvent = ({ event }: CellEventFormatterProps) => {
  const actionType = event.action_type === 'ADD' ? 'added' : 'removed'
  if (event.field_name === 'taglist') {
    return <CompanyAddOrRemoveTagsEvent event={event} actionType={actionType} />
  }
  if (event.field_name === 'contact_tags') {
    return (
      <CompanyAddOrRemoveContactTagsEvent
        event={event}
        actionType={actionType}
      />
    )
  }
  if (event.field_name === 'distributors') {
    return (
      <CompanyAddOrRemoveDistributorsEvent
        event={event}
        actionType={actionType}
      />
    )
  }
  if (event.field_name === 'gpos') {
    return <CompanyAddOrRemoveGPOsEvent event={event} actionType={actionType} />
  }
  if (event.field_name === 'campaigns') {
    return (
      <CompanyAddOrRemoveCampaignsEvent event={event} actionType={actionType} />
    )
  }
}

const CompanyAddOrRemoveTagsEvent = ({
  event,
  actionType,
}: CellEventFormatterProps & { actionType: string }) => {
  const api = useApiService()
  const { data: tags } = api.useGetTagsOptions()

  const tagsMap = useMemo(() => {
    if (!tags) return {}

    return tags.reduce((acc: Record<string, Tag>, curr) => {
      if (!curr.original) return acc
      acc[curr.original.id] = curr.original
      return acc
    }, {})
  }, [tags])

  return (
    <DefaultAddOrRemoveEvent
      action={actionType}
      fieldName={event.value.length === 1 ? 'tag' : 'tags'}
      values={renderTagPills(event.value, tagsMap)}
    />
  )
}

const CompanyAddOrRemoveContactTagsEvent = ({
  event,
  actionType,
}: CellEventFormatterProps & { actionType: string }) => {
  const api = useApiService()
  const { data: tags } = api.useGetTagsOptions(false, 'contacts')

  const tagsMap = useMemo(() => {
    if (!tags) return {}

    return tags.reduce((acc: Record<string, Tag>, curr) => {
      if (!curr.original) return acc
      acc[curr.original.id] = curr.original
      return acc
    }, {})
  }, [tags])

  return (
    <DefaultAddOrRemoveEvent
      action={actionType}
      fieldName={event.value.length === 1 ? 'contact label' : 'contact labels'}
      values={renderTagPills(event.value, tagsMap, '[Label deleted]')}
    />
  )
}

const CompanyAddOrRemoveDistributorsEvent = ({
  event,
  actionType,
}: CellEventFormatterProps & { actionType: string }) => {
  const api = useApiService()
  const { data: distributors } = api.useGetCompanyDistributorsOptions()
  const distributorsMap = useMemo(() => {
    if (!distributors) return {}
    return distributors.reduce(
      (acc: Record<string, CompanyDistributor>, curr) => {
        acc[curr.original.id] = curr.original
        return acc
      },
      {}
    )
  }, [distributors])

  const getDistributors = (distributorsIds: number[]) => {
    if (!Object.keys(distributorsMap).length) return ''
    return distributorsIds
      .map((distributorId) => distributorsMap[distributorId]?.name)
      .join(', ')
  }

  return (
    <DefaultAddOrRemoveEvent
      action={actionType}
      fieldName="distributors"
      values={getDistributors(event.value)}
    />
  )
}

const CompanyAddOrRemoveGPOsEvent = ({
  event,
  actionType,
}: CellEventFormatterProps & { actionType: string }) => {
  const api = useApiService()
  const { data: gpos } = api.useGetCompanyGPOsOptions()
  const gposMap = useMemo(() => {
    if (!gpos) return {}
    return gpos.reduce((acc: Record<string, CompanyGPO>, curr) => {
      acc[curr.original.id] = curr.original
      return acc
    }, {})
  }, [gpos])

  const getGPOs = (gpoIds: number[]) => {
    if (!Object.keys(gposMap).length) return ''
    return gpoIds.map((gpoId) => gposMap[gpoId]?.name).join(', ')
  }

  return (
    <DefaultAddOrRemoveEvent
      action={actionType}
      fieldName="GPOs"
      values={getGPOs(event.value)}
    />
  )
}

const CompanyAddOrRemoveCampaignsEvent = ({
  event,
  actionType,
}: CellEventFormatterProps & { actionType: string }) => {
  const api = useApiService()
  const { data: campaigns } = api.useGetCampaignsOptions()

  const values = campaigns?.find(
    (campaign) => campaign.value === event.value[0]
  )?.label

  const companyWord = event.bulk_size > 1 ? 'companies' : 'company'
  const phrase =
    actionType === 'added'
      ? `Pushed ${companyWord} to campaign`
      : `Removed ${companyWord} from campaign`
  const campaignId = event.value?.[0]

  return (
    <>
      {phrase}:{' '}
      {campaignId && values ? (
        <FbLink to={`/campaigns/${campaignId}`}>{values}</FbLink>
      ) : (
        values
      )}
    </>
  )
}
